import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, computed, effect, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  ChildOnboardingStore,
  UnderageBirthdateErrorPipe,
  UnderageBirthdatePipe,
  UnderageDayPipe,
  UnderageMonthPipe,
  UnderageYearPipe,
} from '@zero/onboarding/domain';
import { TradingSessionComponent } from '@zero/shared/ui-web-components';
import { InputComponent } from '@zero/shared/ui-web-design-system';
import { commonRoutes } from 'constants/web-app-constants';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map } from 'rxjs';
import { DocsComponent } from '../../components/docs/docs.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';

@Component({
  imports: [
    OnboardingContainerComponent,
    TranslocoPipe,
    AsyncPipe,
    UnderageDayPipe,
    UnderageMonthPipe,
    UnderageYearPipe,
    InputComponent,
    TradingSessionComponent,
    DatePipe,
    DocsComponent,
  ],
  templateUrl: './legal-guardian-2-confirmation.component.html',
})
export class LegalGuardian2ConfirmationComponent implements OnInit {
  public store = inject(ChildOnboardingStore);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);

  private readonly routeId = toSignal(
    this.#route.queryParams.pipe(
      filter((params) => !!params),
      map((params) => params['id'] ?? null),
    ),
  );

  public selectedChildOnboarding = computed(() => {
    const id = this.routeId();
    return this.store.childOnboardingEntityMap()[id];
  });

  readonly #birthDay = new BehaviorSubject<string>(undefined);
  readonly #birthMonth = new BehaviorSubject<string>(undefined);
  readonly #birthYear = new BehaviorSubject<string>(undefined);
  readonly #focussedField = new BehaviorSubject<'birthDay' | 'birthMonth' | 'birthYear'>(undefined);
  readonly #sessionAuthorized = new BehaviorSubject<boolean>(false);

  public readonly birthDay$ = this.#birthDay.asObservable();
  public readonly birthMonth$ = this.#birthMonth.asObservable();
  public readonly birthYear$ = this.#birthYear.asObservable();
  public readonly birthdateError$ = combineLatest([this.birthDay$, this.birthMonth$, this.birthYear$]).pipe(
    map(([day, month, year]) => day && month && year && new UnderageBirthdateErrorPipe().transform(day, month, year)),
  );
  public readonly focussedField$ = this.#focussedField.asObservable();
  public readonly sessionAuthorized$ = this.#sessionAuthorized.asObservable();

  constructor() {
    effect(() => {
      const sentLegalGuardian2AcceptSuccessfully = this.store.sentLegalGuardian2AcceptSuccessfully();
      if (sentLegalGuardian2AcceptSuccessfully) {
        this.#router.navigate([commonRoutes.postlogin.child_depot]);
      }
    });
  }

  ngOnInit(): void {
    this.store.loadChildOnboardings();
  }

  public readonly isFormValid$ = combineLatest([
    this.birthDay$,
    this.birthMonth$,
    this.birthYear$,
    this.store.childOnboardingToAccept$,
    this.sessionAuthorized$,
    this.store.sendingLegalGuardian2Check$,
    this.store.sendingLegalGuardian2Accept$,
  ]).pipe(
    map(
      ([
        birthDay,
        birthMonth,
        birthYear,
        childOnboardingToAccept,
        sessionAuthorized,
        sendingLegalGuardian2Check,
        sendingLegalGuardian2Accept,
      ]) =>
        ((new UnderageBirthdatePipe().transform(birthDay, birthMonth, birthYear) && !childOnboardingToAccept) ||
          (!!childOnboardingToAccept && sessionAuthorized)) &&
        !sendingLegalGuardian2Check &&
        !sendingLegalGuardian2Accept,
    ),
  );

  public handleBirthDayChange = (birthDay: string) => {
    this.#birthDay.next(birthDay);
    new UnderageDayPipe().transform(birthDay) && this.#focussedField.next('birthMonth');
  };
  public handleBirthMonthChange = (birthMonth: string) => {
    this.#birthMonth.next(birthMonth);
    new UnderageMonthPipe().transform(birthMonth) && this.#focussedField.next('birthYear');
  };
  public handleBirthYearChange = (birthYear: string) => this.#birthYear.next(birthYear);
  public setSessionAuthState = (sessionAuthorized: boolean) => this.#sessionAuthorized.next(sessionAuthorized);

  protected async submit(): Promise<void> {
    const valid = await firstValueFrom(this.isFormValid$);
    if (!valid) {
      return;
    }
    if (!this.store.childOnboardingToAccept()) {
      this.store.sendLegalGuardian2Check(this.routeId(), `${this.#birthYear.value}-${this.#birthMonth.value}-${this.#birthDay.value}`);
    } else {
      this.store.sendLegalGuardian2Accept();
    }
  }

  public back(): void {
    this.#router.navigate([commonRoutes.postlogin.child_depot]);
  }
}
